'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';

import { destinationsList as localDestinations } from '@/repositories/data/destination';
import { areaList as localAreas } from '@/repositories/data/area';

// Define local types for destinations and areas
interface Destination {
  id: string;
  name: string;
  label: string;
}

interface Area {
  id: string;
  name: string;
  label: string;
  destinationId: string;
}

export default function useSearchQueryParams() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [selectedPerks, setSelectedPerks] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [minBeds, setMinBeds] = useState(0);
  const [minBaths, setMinBaths] = useState(0);
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);

  // Use local data instead of fetching
  const [destinations] = useState<Destination[]>(localDestinations);
  const [areas] = useState<Area[]>([]);
  const [filteredAreas, setFilteredAreas] = useState<Area[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);

  const [date, setDate] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  // Initialize from URL params on load
  useEffect(() => {
    const initFromUrlParams = () => {
      try {
        // Initialize from URL params
        const destinationParam = searchParams.get('destination');
        const areaParam = searchParams.get('area');

        // Set destination if in URL
        if (destinationParam) {
          const foundDestination = destinations.find((d) => d.name === destinationParam);
          if (foundDestination) {
            setSelectedDestination(foundDestination);

            // Filter areas for this destination

            setFilteredAreas(localAreas[foundDestination.name]);

            if (areaParam) {
              const foundArea = localAreas[foundDestination.name].find((a) => a.name === areaParam);
              console.log('foundArea', foundArea);
              if (foundArea) {
                setSelectedArea(foundArea);
              }
            }
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing search data:', error);
        setIsLoading(false);
      }
    };

    initFromUrlParams();
  }, [searchParams, destinations, areas]);

  // Update filtered areas when destination changes
  useEffect(() => {
    if (selectedDestination) {
      setFilteredAreas(localAreas[selectedDestination.name]);

      if (selectedArea) {
        const foundArea = localAreas[selectedDestination.name].find(
          (a) => a.name === selectedArea.name
        );

        if (foundArea) {
          setSelectedArea(foundArea);
        } else {
          setSelectedArea(null);
        }
      }
    } else {
      setFilteredAreas([]);
    }
  }, [selectedDestination]);

  // expose methods to update state
  function handlePerkClick(perk: string) {
    if (selectedPerks.includes(perk)) {
      setSelectedPerks(selectedPerks.filter((p) => p !== perk));
    } else {
      setSelectedPerks([...selectedPerks, perk]);
    }
  }

  function handleDateChange(nextRange: DateRange, selectedDay: Date) {
    setDate((range) => {
      if (range.from && range.to) {
        return { from: selectedDay, to: undefined };
      }
      return nextRange;
    });
  }

  const query = useMemo(() => {
    let query = 'explore?';
    if (minBeds) {
      query += `minBeds=${minBeds}&`;
    }
    if (minBaths) {
      query += `minBaths=${minBaths}&`;
    }
    if (minPrice) {
      query += `minPrice=${minPrice}&`;
    }
    if (maxPrice) {
      query += `maxPrice=${maxPrice}&`;
    }
    if (selectedPerks.length > 0) {
      query += `perks=${selectedPerks.join(',')}&`;
    }

    if (selectedDestination) {
      query += `destination=${selectedDestination?.name}&`;
    }
    if (selectedArea) {
      query += `area=${selectedArea?.name}&`;
    }
    if (date?.from) {
      query += `from=${date.from}&`;
    }
    if (date?.to) {
      query += `to=${date.to}&`;
    }

    if (adults) {
      query += `adults=${adults}&`;
    }

    if (kids) {
      query += `children=${kids}&`;
    }

    return query;
  }, [
    minPrice,
    maxPrice,
    minBeds,
    minBaths,
    selectedPerks,
    date,
    kids,
    adults,
    selectedDestination,
    selectedArea,
  ]);

  useEffect(() => {
    //@ts-expect-error - searchParams is not typed
    for (const [key, value] of searchParams.entries()) {
      if (key === 'minPrice') {
        setMinPrice(Number(value));
      }
      if (key === 'maxPrice') {
        setMaxPrice(Number(value));
      }
      if (key === 'minBeds') {
        setMinBeds(Number(value));
      }
      if (key === 'minBaths') {
        setMinBaths(Number(value));
      }
      if (key === 'perks') {
        setSelectedPerks(value.split(','));
      }

      if (key === 'from') {
        setDate((prev) => ({
          from: new Date(value),
          to: prev.to,
        }));
      }

      if (key === 'to') {
        setDate((prev) => ({
          from: prev.from,
          to: new Date(value),
        }));
      }

      if (key === 'adults') {
        setAdults(Number(value));
      }
      if (key === 'children') {
        setKids(Number(value));
      }
      if (key === 'destination') {
        setSelectedDestination(destinations.find((dest) => dest.name === value) || null);
      }
    }
  }, [searchParams, destinations]);

  function clearSearch() {
    setMinPrice(0);
    setMaxPrice(null);
    setMinBeds(0);
    setMinBaths(0);
    setSelectedPerks([]);

    setKids(0);
    setAdults(1);
    setDate(() => ({ from: undefined, to: undefined }));
    setSelectedDestination(null);
    setSelectedArea(null);

    router.push('/explore');
  }

  async function updateRoute() {
    await router.replace(query, {
      scroll: true,
    });
  }

  return {
    selectedPerks,
    minPrice,
    maxPrice,
    minBeds,
    minBaths,
    handlePerkClick,
    setMinPrice,
    setMaxPrice,
    setMinBeds,
    setMinBaths,
    updateRoute,
    clearSearch,
    date,
    handleDateChange,
    adults,
    kids,
    setAdults,
    setKids,
    destinations,
    selectedDestination,
    setSelectedDestination,
    selectedArea,
    setSelectedArea,
    filteredAreas,
    isLoading,
    setIsLoading,
  };
}
